import { ICompanyFinancialsDataModel } from '../../../data-models/company-financials.data-model';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { ICustomFieldDataDataModel } from '../../../data-models/custom-field-data.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import {
  IMprCustomFieldColumnConfig,
  IMprKpiColumnConfig,
  IMprMetricsColumnConfig,
  MprItem,
  MprPeriod,
} from './multiPeriodReportingUtils';

export interface IMultiPeriodReportingDataPoint extends Pick<
  ICompanyFinancialsDataModel,
  'date' | 'kpiId' | 'period' | 'section'
> {}

export interface IMultiPeriodReportingMetricsDataPoint {
  columnIds: number[];
  date: string;
}

export interface IMultiPeriodReportingCustomDataPoint {
  customFieldIds: number[];
  date: string;
}

export interface IMultiPeriodReportingRequest {
  companyIds?: number[];
  dataPoints: IMultiPeriodReportingDataPoint[]; // kpis
  isDateAdjusted: boolean;
  metricsDataPoints: IMultiPeriodReportingMetricsDataPoint[];
  customDataPoints?: IMultiPeriodReportingCustomDataPoint[];
  fundId: number; // 0 means all funds
}

export const ReportingPeriodType = {
  absolute: 'absolute',
  relative: 'relative',
} as const;
export type ReportingPeriod = (typeof ReportingPeriodType)[keyof typeof ReportingPeriodType];

export type MprColumnConfig = IMprKpiColumnConfig | IMprMetricsColumnConfig | IMprCustomFieldColumnConfig;

export const MprLayout = {
  period: 'period', // one row per company; columns grouped by period, then item
  item: 'item', // one row per company; columns grouped by item, then period
  rows: 'rows', // one row per company and item; one column per period, oldest first (FD-5793)
} as const;
export type MprLayout = (typeof MprLayout)[keyof typeof MprLayout];

export interface IMprViewSettings {
  version: 2;
  items: MprItem[];
  periods: MprPeriod[];
  layout: MprLayout;
  fundId?: number;
}
export function createMprViewSettings(overrides: Partial<IMprViewSettings> = {}): IMprViewSettings {
  return {
    version: 2,
    items: [],
    periods: [],
    layout: MprLayout.period,
    ...overrides,
  };
}

/** Column order of a view saved before FD-5844; only read by the migration. */
export const MprColumnSortType = {
  dateAsc: 'dateAsc', // by periodicity, then date, then name
  displayName: 'displayName', // by name, then date
} as const;
export type MprColumnSortType = (typeof MprColumnSortType)[keyof typeof MprColumnSortType];
/** Shape saved before FD-5844: the expanded column list, in `sortOrder` order unless `columnSort` says otherwise. */
export interface IMprViewSettingsV1 {
  columnSort: MprColumnSortType | null;
  columns: MprColumnConfig[];
  fundId?: number;
}
/** What `view.config.multiPeriodReportingConfig` may hold; see migrateMprViewSettings. */
export type MprViewSettingsStored = IMprViewSettings | IMprViewSettingsV1;

// it IMprMetricsData will contain only those fields/paths that the UI sends as part of POST request
export interface IMprMetricsData extends Partial<IMetricsDataModel> {
  companyId: number;
  date: string;
}

export interface IMultiPeriodReportResponse extends Pick<IMultiPeriodReportingRequest, 'isDateAdjusted'> {
  data: ICompanyFinancialsDataModel[];
  companies: ICompanyDataModel[];
  metricsData: IMprMetricsData[];
  customFieldData: ICustomFieldDataDataModel[];
}

export function createMultiPeriodReportResponse(
  overrides: Partial<IMultiPeriodReportResponse> = {}
): IMultiPeriodReportResponse {
  const { data, companies, metricsData, customFieldData, ...rest } = overrides;
  return {
    isDateAdjusted: true,
    data: data ?? [],
    companies: companies ?? [],
    metricsData: metricsData ?? [],
    customFieldData: customFieldData ?? [],
    ...rest,
  };
}
